import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { usersAPI, ratesAPI, documentTypesAPI, complianceAPI, systemAPI, jobTypesAPI, notificationsAPI, organisationAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { OrganisationNameField } from '../components/OrganisationNameField';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { 
  Users, 
  Settings as SettingsIcon, 
  Plus, 
  Edit, 
  Trash2,
  FileText,
  RefreshCw,
  Building2,
  Clock,
  PoundSterling,
  Bell,
  Database,
  Download,
  Globe,
  Briefcase,
  Image,
  MoreVertical,
  AlertTriangle,
  Send,
  ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { useAuth } from '../context/AuthContext';
import { UserManagementTab, ROLE_STYLES, ROLE_LABELS } from '../components/settings/UserManagementTab';
import { NotificationsTab } from '../components/settings/NotificationsTab';
import { InvitationsTab } from '../components/settings/InvitationsTab';
import SecurityTab from '../components/settings/SecurityTab';
import { CURRENCIES } from '../lib/currency';
import { symbolFor } from '../lib/currency';

export default function Settings() {
  const { user: currentUser, refreshUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [rates, setRates] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [complianceConfig, setComplianceConfig] = useState(null);
  const [systemSettings, setSystemSettings] = useState(null);
  const [systemStats, setSystemStats] = useState(null);
  const [orgCurrency, setOrgCurrency] = useState('GBP');
  const [savingCurrency, setSavingCurrency] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users');
  // Deep-link support: `/settings?tab=invitations` lands on the right tab.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const t = searchParams.get('tab');
    const allowed = new Set([
      'users', 'invitations', 'notifications', 'documents', 'rates',
      'job-types', 'compliance', 'security', 'system',
    ]);
    if (t && allowed.has(t)) setActiveTab(t);
  }, [searchParams]);
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [showRemoveLogoDialog, setShowRemoveLogoDialog] = useState(false);
  
  // User modals
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showEditRoleModal, setShowEditRoleModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Document type modals
  const [showDocTypeModal, setShowDocTypeModal] = useState(false);
  const [editingDocType, setEditingDocType] = useState(null);
  const [docTypeForm, setDocTypeForm] = useState({
    name: '',
    code: '',
    description: '',
    required_for_activation: true,
    is_global: true,
    required_for_job_types: [],
    counts_toward_compliance: true,
    default_expiry_months: ''
  });
  
  // Data management modals
  const [showExportModal, setShowExportModal] = useState(false);
  const [showClearDataModal, setShowClearDataModal] = useState(false);
  const [exportType, setExportType] = useState('all');
  const [exporting, setExporting] = useState(false);
  const [backingUp, setBackingUp] = useState(false);
  const [clearing, setClearing] = useState(false);
  
  // Notification settings
  const [notificationConfig, setNotificationConfig] = useState(null);
  const [expiryPreview, setExpiryPreview] = useState(null);
  const [notificationHistory, setNotificationHistory] = useState([]);
  const [sendingAlerts, setSendingAlerts] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  
  const [newUser, setNewUser] = useState({ email: '', name: '', password: '', role: 'standard' });
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState('');

  const onLogoDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setLogoFile(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps: getLogoRootProps, getInputProps: getLogoInputProps, isDragActive: isLogoDragActive } = useDropzone({
    onDrop: onLogoDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/svg+xml': ['.svg'],
      'image/webp': ['.webp']
    },
    maxFiles: 1,
    maxSize: 2 * 1024 * 1024
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersRes, ratesRes, docTypesRes, jobTypesRes, configRes, settingsRes, statsRes, notifConfigRes, notifPreviewRes, notifHistoryRes, orgRes] = await Promise.all([
        usersAPI.list(),
        ratesAPI.list().catch(() => ({ data: [] })),
        documentTypesAPI.list().catch(() => ({ data: [] })),
        jobTypesAPI.list().catch(() => ({ data: [] })),
        complianceAPI.getConfig().catch(() => ({ data: null })),
        systemAPI.getSettings().catch(() => ({ data: null })),
        systemAPI.getStats().catch(() => ({ data: null })),
        notificationsAPI.getConfig().catch(() => ({ data: null })),
        notificationsAPI.preview(30).catch(() => ({ data: null })),
        notificationsAPI.getHistory(20).catch(() => ({ data: [] })),
        organisationAPI.get().catch(() => ({ data: null })),
      ]);
      setUsers(usersRes.data);
      setRates(ratesRes.data);
      setDocumentTypes(docTypesRes.data);
      setJobTypes(jobTypesRes.data);
      setComplianceConfig(configRes.data);
      setSystemSettings(settingsRes.data);
      setSystemStats(statsRes.data);
      setNotificationConfig(notifConfigRes.data);
      setExpiryPreview(notifPreviewRes.data);
      setNotificationHistory(notifHistoryRes.data);
      setOrgCurrency(orgRes.data?.default_currency || 'GBP');
    } catch (error) {
      console.error('Error fetching settings data:', error);
      toast.error('Failed to load settings data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async () => {
    if (!newUser.email || !newUser.name || !newUser.password) {
      toast.error('Please fill in all fields');
      return;
    }
    try {
      await usersAPI.create(newUser);
      toast.success('User created successfully');
      setShowAddUserModal(false);
      setNewUser({ email: '', name: '', password: '', role: 'standard' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleUpdateRole = async () => {
    if (!selectedUser || !newRole) return;
    try {
      await usersAPI.updateRole(selectedUser.id, newRole);
      toast.success('Role updated successfully');
      setShowEditRoleModal(false);
      setSelectedUser(null);
      setNewRole('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword) {
      toast.error('Please enter a new password');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await usersAPI.resetPassword(selectedUser.id, newPassword);
      toast.success('Password reset successfully');
      setShowResetPasswordModal(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (userId === currentUser?.id) {
      toast.error('Cannot delete your own account');
      return;
    }
    if (!confirm('Are you sure you want to delete this user?')) return;
    try {
      await usersAPI.delete(userId);
      toast.success('User deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const openEditRoleModal = (user) => {
    setSelectedUser(user);
    setNewRole(user.role);
    setShowEditRoleModal(true);
  };

  const openResetPasswordModal = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setShowResetPasswordModal(true);
  };

  // Document type handlers
  const openDocTypeModal = (docType = null) => {
    if (docType) {
      setEditingDocType(docType);
      setDocTypeForm({
        name: docType.name,
        code: docType.code,
        description: docType.description || '',
        required_for_activation: docType.required_for_activation,
        is_global: docType.is_global !== false,
        required_for_job_types: docType.required_for_job_types || [],
        counts_toward_compliance: docType.counts_toward_compliance !== false,
        default_expiry_months: docType.default_expiry_months?.toString() || ''
      });
    } else {
      setEditingDocType(null);
      setDocTypeForm({
        name: '',
        code: '',
        description: '',
        required_for_activation: true,
        is_global: true,
        required_for_job_types: [],
        counts_toward_compliance: true,
        default_expiry_months: ''
      });
    }
    setShowDocTypeModal(true);
  };

  const handleSaveDocType = async () => {
    if (!docTypeForm.name || !docTypeForm.code) {
      toast.error('Please fill in name and code');
      return;
    }
    try {
      const payload = {
        name: docTypeForm.name,
        code: docTypeForm.code.toLowerCase().replace(/\s+/g, '_'),
        description: docTypeForm.description || null,
        required_for_activation: docTypeForm.required_for_activation,
        is_global: docTypeForm.is_global,
        required_for_job_types: docTypeForm.is_global ? [] : docTypeForm.required_for_job_types,
        counts_toward_compliance: docTypeForm.counts_toward_compliance,
        default_expiry_months: docTypeForm.default_expiry_months 
          ? parseInt(docTypeForm.default_expiry_months) 
          : null
      };
      
      if (editingDocType) {
        await documentTypesAPI.update(editingDocType.id, payload);
        toast.success('Document type updated');
      } else {
        await documentTypesAPI.create(payload);
        toast.success('Document type created');
      }
      setShowDocTypeModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save document type');
    }
  };

  const toggleJobTypeRequirement = (jobTypeId) => {
    setDocTypeForm(prev => {
      const current = prev.required_for_job_types || [];
      if (current.includes(jobTypeId)) {
        return { ...prev, required_for_job_types: current.filter(id => id !== jobTypeId) };
      } else {
        return { ...prev, required_for_job_types: [...current, jobTypeId] };
      }
    });
  };

  const handleDeleteDocType = async (typeId) => {
    if (!window.confirm('Are you sure you want to delete this document type?')) return;
    try {
      await documentTypesAPI.delete(typeId);
      toast.success('Document type deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete document type');
    }
  };

  const handleSeedDocTypes = async () => {
    try {
      await complianceAPI.seedDocumentTypes();
      toast.success('Default document types created');
      fetchData();
    } catch (error) {
      toast.error('Failed to seed document types');
    }
  };

  const handleUpdateComplianceConfig = async (key, value) => {
    try {
      await complianceAPI.updateConfig({ [key]: value });
      toast.success('Compliance configuration updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update configuration');
    }
  };

  // Notification handlers
  const handleSendExpiryAlerts = async () => {
    try {
      setSendingAlerts(true);
      const res = await notificationsAPI.sendExpiryAlerts({
        send_to_medics: true,
        send_admin_digest: true,
        days_threshold: 30
      });
      toast.success(`Alerts sent! Medics: ${res.data.result.medic_emails_sent}, Admins: ${res.data.result.admin_emails_sent}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send alerts');
    } finally {
      setSendingAlerts(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmailAddress) {
      toast.error('Please enter an email address');
      return;
    }
    try {
      setSendingTestEmail(true);
      await notificationsAPI.sendTestEmail({
        recipient_email: testEmailAddress,
        include_sample_data: true
      });
      toast.success(`Test email sent to ${testEmailAddress}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTestEmail(false);
    }
  };

  // System settings handlers
  const handleUpdateSystemSettings = async (key, value, opts = {}) => {
    const { silent = false } = opts;
    try {
      if (!silent) setSaving(true);
      await systemAPI.updateSettings({ [key]: value });
      setSystemSettings(prev => ({ ...prev, [key]: value }));
      // iter73.1 — tell the global BrandingContext to refetch so the
      // sidebar/title update live without a reload.
      if (key === 'organisation_name' || key === 'organisation_logo_url') {
        window.dispatchEvent(new CustomEvent('elara:branding-refreshed'));
      }
      if (!silent) toast.success('Settings updated');
    } catch (error) {
      if (!silent) toast.error('Failed to update settings');
      throw error;
    } finally {
      if (!silent) setSaving(false);
    }
  };

  const handleUpdateCurrency = async (code) => {
    const prev = orgCurrency;
    try {
      setSavingCurrency(true);
      setOrgCurrency(code); // optimistic
      await organisationAPI.updateCurrency(code);
      // Pull refreshed user so `user.organisation_currency` is current
      // for every component that reads it from AuthContext.
      try { await refreshUser(); } catch (_) {}
      toast.success(`Currency set to ${code}. Change applies to new records only.`);
    } catch (error) {
      setOrgCurrency(prev);
      toast.error(error.response?.data?.detail || 'Failed to update currency');
    } finally {
      setSavingCurrency(false);
    }
  };

  const [sendingDigest, setSendingDigest] = useState(false);
  const handleSendDigestNow = async () => {
    try {
      setSendingDigest(true);
      const res = await systemAPI.sendWeeklyDigest();
      const d = res.data || {};
      if (d.skipped) {
        toast.warning(`Digest skipped: ${d.skipped}`);
      } else {
        toast.success(`Weekly digest sent to ${d.sent ?? 0} manager${(d.sent ?? 0) === 1 ? '' : 's'}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send digest');
    } finally {
      setSendingDigest(false);
    }
  };

  const handleUploadLogo = async () => {
    if (!logoFile) return;
    try {
      setSaving(true);
      await systemAPI.uploadLogo(logoFile);
      toast.success('Logo uploaded successfully');
      setLogoFile(null);
      fetchData();
      window.dispatchEvent(new CustomEvent('elara:branding-refreshed'));
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveLogo = async () => {
    try {
      setSaving(true);
      await systemAPI.deleteLogo();
      toast.success('Logo removed');
      fetchData();
      window.dispatchEvent(new CustomEvent('elara:branding-refreshed'));
    } catch (error) {
      toast.error('Failed to remove logo');
    } finally {
      setSaving(false);
      setShowRemoveLogoDialog(false);
    }
  };

  const handleExportData = async () => {
    try {
      setExporting(true);
      const response = await systemAPI.exportData({ export_type: exportType });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `elara_export_${exportType}_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Data exported successfully');
      setShowExportModal(false);
    } catch (error) {
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const handleBackup = async () => {
    try {
      setBackingUp(true);
      const response = await systemAPI.triggerBackup();
      toast.success(`Backup created: ${response.data.backup_id}`);
    } catch (error) {
      toast.error('Failed to create backup');
    } finally {
      setBackingUp(false);
    }
  };

  const handleClearTestData = async () => {
    try {
      setClearing(true);
      const response = await systemAPI.clearTestData();
      toast.success(response.data.message);
      setShowClearDataModal(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to clear test data');
    } finally {
      setClearing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-[#1ABC9C]/20 border-t-[#1ABC9C] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-container">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        {/* Scrollable rail so all 6 tabs stay reachable on mobile
            instead of being clipped by the viewport (iter71.4). */}
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-thin">
          <TabsList className="bg-[#2B2B2B] inline-flex w-max whitespace-nowrap">
            <TabsTrigger value="users" className="data-[state=active]:bg-[#1ABC9C] data-[state=active]:text-white shrink-0">
              <Users className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">User Management</span>
              <span className="sm:hidden">Users</span>
            </TabsTrigger>
            <TabsTrigger value="invitations" className="data-[state=active]:bg-[#1ABC9C] data-[state=active]:text-white shrink-0" data-testid="invitations-tab-trigger">
              <Send className="w-4 h-4 mr-2" />
              Invitations
            </TabsTrigger>
            <TabsTrigger value="compliance" className="data-[state=active]:bg-[#1ABC9C] data-[state=active]:text-white shrink-0">
              <FileText className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Compliance Config</span>
              <span className="sm:hidden">Compliance</span>
            </TabsTrigger>
            <TabsTrigger value="notifications" className="data-[state=active]:bg-[#1ABC9C] data-[state=active]:text-white shrink-0">
              <Bell className="w-4 h-4 mr-2" />
              Notifications
            </TabsTrigger>
            <TabsTrigger value="security" className="data-[state=active]:bg-[#1ABC9C] data-[state=active]:text-white shrink-0" data-testid="security-tab-trigger">
              <ShieldCheck className="w-4 h-4 mr-2" />
              Security
            </TabsTrigger>
            <TabsTrigger value="system" className="data-[state=active]:bg-[#1ABC9C] data-[state=active]:text-white shrink-0">
              <SettingsIcon className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">System Settings</span>
              <span className="sm:hidden">System</span>
            </TabsTrigger>
          </TabsList>
        </div>

        {/* User Management Tab */}
        <TabsContent value="users" className="mt-6">
          <UserManagementTab
            users={users}
            currentUser={currentUser}
            onAddUser={() => setShowAddUserModal(true)}
            onEditRole={openEditRoleModal}
            onResetPassword={openResetPasswordModal}
            onDeleteUser={handleDeleteUser}
          />
        </TabsContent>

        {/* Invitations Tab */}
        <TabsContent value="invitations" className="mt-6">
          <InvitationsTab />
        </TabsContent>

        {/* Compliance Configuration Tab */}
        <TabsContent value="compliance" className="mt-6">
          <div className="space-y-6">
            {/* Document Types */}
            <Card className="bg-[#2B2B2B] border-white/5">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-[#F2F2F2]">Document Types</CardTitle>
                <div className="flex gap-2">
                  {documentTypes.length === 0 && (
                    <Button
                      variant="outline"
                      className="border-white/10"
                      onClick={handleSeedDocTypes}
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Load Defaults
                    </Button>
                  )}
                  <Button
                    className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90"
                    onClick={() => openDocTypeModal()}
                    data-testid="add-doc-type-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Type
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/5 hover:bg-transparent">
                      <TableHead className="text-[#A1A1AA]">Name</TableHead>
                      <TableHead className="text-[#A1A1AA]">Code</TableHead>
                      <TableHead className="text-[#A1A1AA]">Scope</TableHead>
                      <TableHead className="text-[#A1A1AA]">Required</TableHead>
                      <TableHead className="text-[#A1A1AA]">Expiry</TableHead>
                      <TableHead className="text-[#A1A1AA] text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {documentTypes.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-[#A1A1AA] py-8">
                          No document types configured. Click &quot;Load Defaults&quot; to add standard types.
                        </TableCell>
                      </TableRow>
                    ) : (
                      documentTypes.map((docType) => (
                        <TableRow key={docType.id} className="border-white/5 hover:bg-white/5" data-testid={`doc-type-row-${docType.code}`}>
                          <TableCell className="text-[#F2F2F2] font-medium">
                            <div className="flex items-center gap-2">
                              {docType.name}
                              {docType.counts_toward_compliance === false && (
                                <Badge className="bg-gray-500/15 text-gray-400 border-gray-500/20 text-xs">
                                  No %
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-[#A1A1AA] font-mono text-sm">
                            {docType.code}
                          </TableCell>
                          <TableCell>
                            {docType.is_global !== false ? (
                              <Badge className="bg-blue-500/15 text-blue-400 border-blue-500/20">
                                <Globe className="w-3 h-3 mr-1" />
                                Global
                              </Badge>
                            ) : (
                              <Badge className="bg-purple-500/15 text-purple-400 border-purple-500/20">
                                <Briefcase className="w-3 h-3 mr-1" />
                                {docType.required_for_job_types?.length || 0} Job Types
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge className={docType.required_for_activation 
                              ? 'bg-red-500/15 text-red-400 border-red-500/20' 
                              : 'bg-gray-500/15 text-gray-400 border-gray-500/20'
                            }>
                              {docType.required_for_activation ? 'Required' : 'Optional'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-[#A1A1AA]">
                            {docType.default_expiry_months 
                              ? `${docType.default_expiry_months} months` 
                              : 'No expiry'
                            }
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="text-[#A1A1AA] hover:text-[#F2F2F2]">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="bg-[#2B2B2B] border-white/10">
                                <DropdownMenuItem onClick={() => openDocTypeModal(docType)} className="text-[#F2F2F2] focus:bg-white/10">
                                  <Edit className="w-4 h-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDeleteDocType(docType.id)} className="text-red-400 focus:bg-white/10">
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Compliance Settings */}
            <Card className="bg-[#2B2B2B] border-white/5">
              <CardHeader>
                <CardTitle className="text-[#F2F2F2]">Compliance Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-[#1A1A1A] rounded-lg">
                  <div>
                    <p className="text-[#F2F2F2] font-medium">Warning Window</p>
                    <p className="text-sm text-[#A1A1AA]">Days before expiry to show warning</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={complianceConfig?.warning_window_days || 30}
                      onChange={(e) => handleUpdateComplianceConfig('warning_window_days', parseInt(e.target.value))}
                      className="w-20 bg-[#2B2B2B] border-white/10"
                    />
                    <span className="text-[#A1A1AA]">days</span>
                  </div>
                </div>
                <div className="flex items-center justify-between p-4 bg-[#1A1A1A] rounded-lg">
                  <div>
                    <p className="text-[#F2F2F2] font-medium">Require All Documents</p>
                    <p className="text-sm text-[#A1A1AA]">All required documents must be approved for activation</p>
                  </div>
                  <Switch 
                    checked={complianceConfig?.require_all_documents ?? true}
                    onCheckedChange={(checked) => handleUpdateComplianceConfig('require_all_documents', checked)}
                  />
                </div>
                <div className="flex items-center justify-between p-4 bg-[#1A1A1A] rounded-lg">
                  <div>
                    <p className="text-[#F2F2F2] font-medium">Allow Expired Override</p>
                    <p className="text-sm text-[#A1A1AA]">Allow scheduling medics with expired documents (with warning)</p>
                  </div>
                  <Switch 
                    checked={complianceConfig?.allow_expired_override ?? true}
                    onCheckedChange={(checked) => handleUpdateComplianceConfig('allow_expired_override', checked)}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="mt-6" data-testid="notifications-tab">
          <NotificationsTab
            notificationConfig={notificationConfig}
            expiryPreview={expiryPreview}
            notificationHistory={notificationHistory}
            testEmailAddress={testEmailAddress}
            setTestEmailAddress={setTestEmailAddress}
            sendingTestEmail={sendingTestEmail}
            sendingAlerts={sendingAlerts}
            onSendTestEmail={handleSendTestEmail}
            onSendExpiryAlerts={handleSendExpiryAlerts}
            systemSettings={systemSettings}
            onUpdateSystemSettings={handleUpdateSystemSettings}
            sendingDigest={sendingDigest}
            onSendDigestNow={handleSendDigestNow}
          />
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security" className="mt-6" data-testid="security-tab-content">
          <SecurityTab />
        </TabsContent>

        {/* System Settings Tab */}
        <TabsContent value="system" className="mt-6">
          <div className="grid gap-6">
            {/* App Maintenance — one-tap refresh for admins after a
                config change (iter71.2 — Settings Refresh). Clears
                service-worker caches and reloads so the latest build
                / settings are picked up without clearing site data
                manually. */}
            <Card className="bg-[#2B2B2B] border-white/5" data-testid="app-maintenance-card">
              <CardHeader>
                <CardTitle className="text-[#F2F2F2] flex items-center gap-2">
                  <RefreshCw className="w-5 h-5 text-[#1ABC9C]" />
                  App Maintenance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between p-4 bg-[#1A1A1A] rounded-lg">
                  <div>
                    <p className="text-[#F2F2F2] font-medium">Refresh App</p>
                    <p className="text-sm text-[#A1A1AA]">
                      Clears cached assets and reloads the latest version. Use after changing a setting that doesn't appear to take effect.
                    </p>
                  </div>
                  <Button
                    onClick={async () => {
                      try {
                        if ('caches' in window) {
                          const keys = await caches.keys();
                          await Promise.all(keys.map(k => caches.delete(k)));
                        }
                        if ('serviceWorker' in navigator) {
                          const regs = await navigator.serviceWorker.getRegistrations();
                          await Promise.all(regs.map(r => r.unregister()));
                        }
                      } catch (e) {
                        console.warn('cache clear failed', e);
                      } finally {
                        toast.success('Refreshing...');
                        setTimeout(() => window.location.reload(true), 400);
                      }
                    }}
                    className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90"
                    data-testid="refresh-app-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh App
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Organisation Settings */}
            <Card className="bg-[#2B2B2B] border-white/5">
              <CardHeader>
                <CardTitle className="text-[#F2F2F2] flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-[#1ABC9C]" />
                  Organisation Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-[#1A1A1A] rounded-lg">
                  <div>
                    <p className="text-[#F2F2F2] font-medium">Organisation Name</p>
                    <p className="text-sm text-[#A1A1AA]">Displayed across the application</p>
                  </div>
                  <OrganisationNameField
                    value={systemSettings?.organisation_name ?? ''}
                    onSave={(v) => handleUpdateSystemSettings('organisation_name', v, { silent: true })}
                  />
                </div>
                <div className="p-4 bg-[#1A1A1A] rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-[#F2F2F2] font-medium">Organisation Logo</p>
                      <p className="text-sm text-[#A1A1AA]">Upload your company logo (max 2MB)</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {systemSettings?.organisation_logo_url ? (
                      <div
                        className="w-20 h-20 rounded-lg border border-white/10 overflow-hidden bg-[#2B2B2B] flex items-center justify-center p-2"
                        data-testid="logo-preview"
                      >
                        <img
                          src={systemSettings.organisation_logo_url}
                          alt="Organisation logo preview"
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                    ) : (
                      <div
                        className="w-20 h-20 rounded-lg border border-dashed border-white/10 bg-[#2B2B2B] flex flex-col items-center justify-center gap-1"
                        data-testid="logo-preview-empty"
                      >
                        <Image className="w-6 h-6 text-[#A1A1AA]" />
                        <span className="text-[10px] text-[#A1A1AA]">No logo</span>
                      </div>
                    )}
                    <div className="flex-1 space-y-2">
                      <div
                        {...getLogoRootProps()}
                        className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                          isLogoDragActive ? 'border-[#1ABC9C] bg-[#1ABC9C]/10' : 'border-white/20 hover:border-white/40'
                        }`}
                      >
                        <input {...getLogoInputProps()} />
                        {logoFile ? (
                          <div className="flex items-center justify-between" onClick={(e) => e.stopPropagation()}>
                            <span className="text-[#F2F2F2]">{logoFile.name}</span>
                            <Button size="sm" onClick={(e) => { e.stopPropagation(); handleUploadLogo(); }} disabled={saving} className="bg-[#1ABC9C]">
                              {saving ? 'Uploading...' : 'Upload'}
                            </Button>
                          </div>
                        ) : (
                          <p className="text-sm text-[#A1A1AA]">Drop logo here or click to select</p>
                        )}
                      </div>
                      {systemSettings?.organisation_logo_url && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowRemoveLogoDialog(true)}
                          disabled={saving}
                          className="text-xs text-[#A1A1AA] hover:text-red-400 hover:bg-red-500/5"
                          data-testid="remove-logo-btn"
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                          Remove logo
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between p-4 bg-[#1A1A1A] rounded-lg" data-testid="org-currency-row">
                  <div>
                    <p className="text-[#F2F2F2] font-medium">Default Currency</p>
                    <p className="text-sm text-[#A1A1AA]">
                      Applied to <span className="text-[#1ABC9C]">new</span> shifts & expenses only. Existing records keep their original currency.
                    </p>
                  </div>
                  <Select
                    value={orgCurrency}
                    onValueChange={handleUpdateCurrency}
                    disabled={savingCurrency}
                  >
                    <SelectTrigger className="w-56 bg-[#2B2B2B] border-white/10" data-testid="org-currency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#2B2B2B] border-white/10 max-h-80">
                      {CURRENCIES.map(c => (
                        <SelectItem key={c.code} value={c.code} data-testid={`currency-option-${c.code}`}>
                          {c.symbol}  {c.code} — {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between p-4 bg-[#1A1A1A] rounded-lg">
                  <div>
                    <p className="text-[#F2F2F2] font-medium">Timezone</p>
                    <p className="text-sm text-[#A1A1AA]">Default timezone for scheduling</p>
                  </div>
                  <Select 
                    value={systemSettings?.timezone || 'Europe/London'} 
                    onValueChange={(v) => handleUpdateSystemSettings('timezone', v)}
                  >
                    <SelectTrigger className="w-48 bg-[#2B2B2B] border-white/10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#2B2B2B] border-white/10">
                      <SelectItem value="Europe/London">Europe/London (GMT)</SelectItem>
                      <SelectItem value="Europe/Paris">Europe/Paris (CET)</SelectItem>
                      <SelectItem value="America/New_York">America/New York (EST)</SelectItem>
                      <SelectItem value="America/Los_Angeles">America/Los Angeles (PST)</SelectItem>
                      <SelectItem value="Asia/Dubai">Asia/Dubai (GST)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Shift Settings */}
            <Card className="bg-[#2B2B2B] border-white/5">
              <CardHeader>
                <CardTitle className="text-[#F2F2F2] flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#1ABC9C]" />
                  Shift Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-[#1A1A1A] rounded-lg">
                  <div>
                    <p className="text-[#F2F2F2] font-medium">Default Shift Duration</p>
                    <p className="text-sm text-[#A1A1AA]">Default hours for new shifts</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={systemSettings?.default_shift_duration_hours || 8}
                      onChange={(e) => handleUpdateSystemSettings('default_shift_duration_hours', parseInt(e.target.value))}
                      className="w-20 bg-[#2B2B2B] border-white/10"
                    />
                    <span className="text-[#A1A1AA]">hours</span>
                  </div>
                </div>
                <div className="flex items-center justify-between p-4 bg-[#1A1A1A] rounded-lg">
                  <div>
                    <p className="text-[#F2F2F2] font-medium">Minimum Notice Period</p>
                    <p className="text-sm text-[#A1A1AA]">Required notice for shift changes</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={systemSettings?.minimum_notice_hours || 24}
                      onChange={(e) => handleUpdateSystemSettings('minimum_notice_hours', parseInt(e.target.value))}
                      className="w-20 bg-[#2B2B2B] border-white/10"
                    />
                    <span className="text-[#A1A1AA]">hours</span>
                  </div>
                </div>
                <div className="flex items-center justify-between p-4 bg-[#1A1A1A] rounded-lg">
                  <div>
                    <p className="text-[#F2F2F2] font-medium">Auto-Assign</p>
                    <p className="text-sm text-[#A1A1AA]">Automatically assign available medics to shifts</p>
                  </div>
                  <Switch 
                    checked={systemSettings?.auto_assign_enabled ?? false}
                    onCheckedChange={(checked) => handleUpdateSystemSettings('auto_assign_enabled', checked)}
                  />
                </div>
                <div className="flex items-center justify-between p-4 bg-[#1A1A1A] rounded-lg" data-testid="overtime-cap-row">
                  <div>
                    <p className="text-[#F2F2F2] font-medium">Weekly Hours Cap</p>
                    <p className="text-sm text-[#A1A1AA]">Warn managers when a medic exceeds this many hours per week</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={systemSettings?.max_weekly_hours ?? 48}
                      onChange={(e) => handleUpdateSystemSettings('max_weekly_hours', parseFloat(e.target.value))}
                      className="w-24 bg-[#2B2B2B] border-white/10"
                      data-testid="max-weekly-hours-input"
                    />
                    <span className="text-[#A1A1AA]">hours</span>
                  </div>
                </div>
                <div className="flex items-center justify-between p-4 bg-[#1A1A1A] rounded-lg" data-testid="rest-gap-row">
                  <div>
                    <p className="text-[#F2F2F2] font-medium">Minimum Rest Between Shifts</p>
                    <p className="text-sm text-[#A1A1AA]">Warn managers when consecutive shifts leave less rest than this</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={systemSettings?.min_rest_hours_between_shifts ?? 11}
                      onChange={(e) => handleUpdateSystemSettings('min_rest_hours_between_shifts', parseFloat(e.target.value))}
                      className="w-24 bg-[#2B2B2B] border-white/10"
                      data-testid="min-rest-hours-input"
                    />
                    <span className="text-[#A1A1AA]">hours</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Rate Settings */}
            <Card className="bg-[#2B2B2B] border-white/5">
              <CardHeader>
                <CardTitle className="text-[#F2F2F2] flex items-center gap-2">
                  <PoundSterling className="w-5 h-5 text-[#1ABC9C]" />
                  Rate Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-[#1A1A1A] rounded-lg">
                  <div>
                    <p className="text-[#F2F2F2] font-medium">Default Hourly Rate</p>
                    <p className="text-sm text-[#A1A1AA]">Base rate for new medics</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[#A1A1AA]">{symbolFor(orgCurrency)}</span>
                    <Input
                      type="number"
                      step="0.01"
                      value={systemSettings?.default_hourly_rate || 15}
                      onChange={(e) => handleUpdateSystemSettings('default_hourly_rate', parseFloat(e.target.value))}
                      className="w-24 bg-[#2B2B2B] border-white/10"
                    />
                    <span className="text-[#A1A1AA]">/hour</span>
                  </div>
                </div>
                <div className="flex items-center justify-between p-4 bg-[#1A1A1A] rounded-lg">
                  <div>
                    <p className="text-[#F2F2F2] font-medium">Overtime Multiplier</p>
                    <p className="text-sm text-[#A1A1AA]">Rate multiplier for overtime hours</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      step="0.1"
                      value={systemSettings?.overtime_multiplier || 1.5}
                      onChange={(e) => handleUpdateSystemSettings('overtime_multiplier', parseFloat(e.target.value))}
                      className="w-20 bg-[#2B2B2B] border-white/10"
                    />
                    <span className="text-[#A1A1AA]">x</span>
                  </div>
                </div>
                <div className="flex items-center justify-between p-4 bg-[#1A1A1A] rounded-lg">
                  <div>
                    <p className="text-[#F2F2F2] font-medium">Holiday Pay Multiplier</p>
                    <p className="text-sm text-[#A1A1AA]">Rate multiplier for holiday shifts</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      step="0.1"
                      value={systemSettings?.holiday_pay_multiplier || 2.0}
                      onChange={(e) => handleUpdateSystemSettings('holiday_pay_multiplier', parseFloat(e.target.value))}
                      className="w-20 bg-[#2B2B2B] border-white/10"
                    />
                    <span className="text-[#A1A1AA]">x</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Notification Settings */}
            <Card className="bg-[#2B2B2B] border-white/5">
              <CardHeader>
                <CardTitle className="text-[#F2F2F2] flex items-center gap-2">
                  <Bell className="w-5 h-5 text-[#1ABC9C]" />
                  Notification Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-[#1A1A1A] rounded-lg">
                  <div>
                    <p className="text-[#F2F2F2] font-medium">Email Notifications</p>
                    <p className="text-sm text-[#A1A1AA]">Enable email notifications globally</p>
                  </div>
                  <Switch 
                    checked={systemSettings?.email_notifications_enabled ?? true}
                    onCheckedChange={(checked) => handleUpdateSystemSettings('email_notifications_enabled', checked)}
                  />
                </div>
                <div className="flex items-center justify-between p-4 bg-[#1A1A1A] rounded-lg">
                  <div>
                    <p className="text-[#F2F2F2] font-medium">Shift Reminders</p>
                    <p className="text-sm text-[#A1A1AA]">Send reminders before shift starts</p>
                  </div>
                  <Switch 
                    checked={systemSettings?.shift_reminder_enabled ?? true}
                    onCheckedChange={(checked) => handleUpdateSystemSettings('shift_reminder_enabled', checked)}
                  />
                </div>
                <div className="flex items-center justify-between p-4 bg-[#1A1A1A] rounded-lg">
                  <div>
                    <p className="text-[#F2F2F2] font-medium">Compliance Alerts</p>
                    <p className="text-sm text-[#A1A1AA]">Alert when documents expire</p>
                  </div>
                  <Switch 
                    checked={systemSettings?.compliance_alerts_enabled ?? true}
                    onCheckedChange={(checked) => handleUpdateSystemSettings('compliance_alerts_enabled', checked)}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Data Management */}
            <Card className="bg-[#2B2B2B] border-white/5">
              <CardHeader>
                <CardTitle className="text-[#F2F2F2] flex items-center gap-2">
                  <Database className="w-5 h-5 text-[#1ABC9C]" />
                  Data Management
                </CardTitle>
                <CardDescription className="text-[#A1A1AA]">
                  Export data, create backups, and manage test data
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Stats */}
                {systemStats && (
                  <div className="grid grid-cols-4 gap-3 mb-4">
                    <div className="p-3 bg-[#1A1A1A] rounded-lg text-center">
                      <p className="text-2xl font-bold text-[#1ABC9C]">{systemStats.users}</p>
                      <p className="text-xs text-[#A1A1AA]">Users</p>
                    </div>
                    <div className="p-3 bg-[#1A1A1A] rounded-lg text-center">
                      <p className="text-2xl font-bold text-[#1ABC9C]">{systemStats.medics}</p>
                      <p className="text-xs text-[#A1A1AA]">Medics</p>
                    </div>
                    <div className="p-3 bg-[#1A1A1A] rounded-lg text-center">
                      <p className="text-2xl font-bold text-[#1ABC9C]">{systemStats.shifts}</p>
                      <p className="text-xs text-[#A1A1AA]">Shifts</p>
                    </div>
                    <div className="p-3 bg-[#1A1A1A] rounded-lg text-center">
                      <p className="text-2xl font-bold text-[#1ABC9C]">{systemStats.audit_logs}</p>
                      <p className="text-xs text-[#A1A1AA]">Audit Logs</p>
                    </div>
                  </div>
                )}

                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    className="flex-1 border-white/10"
                    onClick={() => setShowExportModal(true)}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export Data
                  </Button>
                  <Button 
                    variant="outline" 
                    className="flex-1 border-white/10"
                    onClick={handleBackup}
                    disabled={backingUp}
                  >
                    <Database className="w-4 h-4 mr-2" />
                    {backingUp ? 'Creating...' : 'Create Backup'}
                  </Button>
                  <Button 
                    variant="outline" 
                    className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10"
                    onClick={() => setShowClearDataModal(true)}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Clear Test Data
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add User Modal */}
      <Dialog open={showAddUserModal} onOpenChange={setShowAddUserModal}>
        <DialogContent className="bg-[#2B2B2B] border-white/10 text-[#F2F2F2]">
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={newUser.name}
                onChange={(e) => setNewUser(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Full name"
                className="bg-[#1A1A1A] border-white/10"
                data-testid="new-user-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                placeholder="email@example.com"
                className="bg-[#1A1A1A] border-white/10"
                data-testid="new-user-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                placeholder="Minimum 6 characters"
                className="bg-[#1A1A1A] border-white/10"
                data-testid="new-user-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={newUser.role} onValueChange={(value) => setNewUser(prev => ({ ...prev, role: value }))}>
                <SelectTrigger className="bg-[#1A1A1A] border-white/10" data-testid="new-user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#2B2B2B] border-white/10">
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="team_leader">Manager</SelectItem>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="medic">Medic</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAddUserModal(false)} className="text-[#A1A1AA]">
              Cancel
            </Button>
            <Button onClick={handleAddUser} className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90" data-testid="save-user-btn">
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Role Modal */}
      <Dialog open={showEditRoleModal} onOpenChange={setShowEditRoleModal}>
        <DialogContent className="bg-[#2B2B2B] border-white/10 text-[#F2F2F2]">
          <DialogHeader>
            <DialogTitle>Change User Role</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-[#A1A1AA]">
              Changing role for: <span className="text-[#F2F2F2] font-medium">{selectedUser?.name}</span>
            </p>
            <div className="space-y-2">
              <Label>New Role</Label>
              <Select value={newRole} onValueChange={setNewRole}>
                <SelectTrigger className="bg-[#1A1A1A] border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#2B2B2B] border-white/10">
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="team_leader">Manager</SelectItem>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="medic">Medic</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowEditRoleModal(false)} className="text-[#A1A1AA]">
              Cancel
            </Button>
            <Button onClick={handleUpdateRole} className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90">
              Update Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Modal */}
      <Dialog open={showResetPasswordModal} onOpenChange={setShowResetPasswordModal}>
        <DialogContent className="bg-[#2B2B2B] border-white/10 text-[#F2F2F2]">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-[#A1A1AA]">
              Resetting password for: <span className="text-[#F2F2F2] font-medium">{selectedUser?.name}</span>
            </p>
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Minimum 6 characters"
                className="bg-[#1A1A1A] border-white/10"
                data-testid="reset-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowResetPasswordModal(false)} className="text-[#A1A1AA]">
              Cancel
            </Button>
            <Button onClick={handleResetPassword} className="bg-[#FFC04D] hover:bg-[#FFC04D]/90 text-black" data-testid="reset-password-btn">
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Type Modal */}
      <Dialog open={showDocTypeModal} onOpenChange={setShowDocTypeModal}>
        <DialogContent className="bg-[#2B2B2B] border-white/10 text-[#F2F2F2] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingDocType ? 'Edit Document Type' : 'Add Document Type'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={docTypeForm.name}
                onChange={(e) => setDocTypeForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., DBS Certificate"
                className="bg-[#1A1A1A] border-white/10"
                data-testid="doc-type-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Code</Label>
              <Input
                value={docTypeForm.code}
                onChange={(e) => setDocTypeForm(prev => ({ ...prev, code: e.target.value }))}
                placeholder="e.g., dbs"
                className="bg-[#1A1A1A] border-white/10"
                disabled={!!editingDocType}
                data-testid="doc-type-code-input"
              />
              <p className="text-xs text-[#A1A1AA]">Unique identifier (lowercase, no spaces)</p>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={docTypeForm.description}
                onChange={(e) => setDocTypeForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description..."
                className="bg-[#1A1A1A] border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label>Default Expiry (months)</Label>
              <Input
                type="number"
                value={docTypeForm.default_expiry_months}
                onChange={(e) => setDocTypeForm(prev => ({ ...prev, default_expiry_months: e.target.value }))}
                placeholder="Leave blank for no expiry"
                className="bg-[#1A1A1A] border-white/10"
              />
              <p className="text-xs text-[#A1A1AA]">Users can override this with per-upload expiry dates</p>
            </div>

            {/* Scope Configuration */}
            <div className="space-y-3 p-3 bg-[#1A1A1A] rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#F2F2F2] flex items-center gap-2">
                    <Globe className="w-4 h-4 text-blue-400" />
                    Global Requirement
                  </p>
                  <p className="text-xs text-[#A1A1AA]">Required for ALL medics regardless of job type</p>
                </div>
                <Switch 
                  checked={docTypeForm.is_global}
                  onCheckedChange={(checked) => setDocTypeForm(prev => ({ ...prev, is_global: checked }))}
                  data-testid="doc-type-global-switch"
                />
              </div>

              {/* Job Type Selection (only shown when not global) */}
              {!docTypeForm.is_global && (
                <div className="mt-3 pt-3 border-t border-white/10">
                  <p className="text-sm text-[#F2F2F2] mb-2 flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-purple-400" />
                    Required for Job Types:
                  </p>
                  {jobTypes.length === 0 ? (
                    <p className="text-xs text-[#A1A1AA]">No job types configured</p>
                  ) : (
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {jobTypes.map(jt => (
                        <div key={jt.id} className="flex items-center gap-2">
                          <Checkbox
                            id={`jt-${jt.id}`}
                            checked={(docTypeForm.required_for_job_types || []).includes(jt.id)}
                            onCheckedChange={() => toggleJobTypeRequirement(jt.id)}
                          />
                          <label htmlFor={`jt-${jt.id}`} className="text-sm text-[#F2F2F2] cursor-pointer">
                            {jt.name}
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between p-3 bg-[#1A1A1A] rounded-lg">
              <div>
                <p className="text-[#F2F2F2]">Required for Activation</p>
                <p className="text-xs text-[#A1A1AA]">Must be approved before medic can be activated</p>
              </div>
              <Switch 
                checked={docTypeForm.required_for_activation}
                onCheckedChange={(checked) => setDocTypeForm(prev => ({ ...prev, required_for_activation: checked }))}
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-[#1A1A1A] rounded-lg">
              <div>
                <p className="text-[#F2F2F2]">Counts Toward Compliance %</p>
                <p className="text-xs text-[#A1A1AA]">Include in compliance percentage calculation</p>
              </div>
              <Switch 
                checked={docTypeForm.counts_toward_compliance}
                onCheckedChange={(checked) => setDocTypeForm(prev => ({ ...prev, counts_toward_compliance: checked }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDocTypeModal(false)} className="text-[#A1A1AA]">
              Cancel
            </Button>
            <Button onClick={handleSaveDocType} className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90" data-testid="save-doc-type-btn">
              {editingDocType ? 'Save Changes' : 'Create Type'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Data Modal */}
      <Dialog open={showExportModal} onOpenChange={setShowExportModal}>
        <DialogContent className="bg-[#2B2B2B] border-white/10 text-[#F2F2F2]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="w-5 h-5 text-[#1ABC9C]" />
              Export Data
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Export your data to CSV format
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Export Type</Label>
              <Select value={exportType} onValueChange={setExportType}>
                <SelectTrigger className="bg-[#1A1A1A] border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#2B2B2B] border-white/10">
                  <SelectItem value="all">All Data</SelectItem>
                  <SelectItem value="medics">Medics Only</SelectItem>
                  <SelectItem value="shifts">Shifts Only</SelectItem>
                  <SelectItem value="expenses">Expenses Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="p-3 bg-[#1A1A1A] rounded-lg">
              <p className="text-sm text-[#A1A1AA]">
                {exportType === 'all' 
                  ? 'This will export medics, shifts, expenses, and documents.'
                  : `This will export all ${exportType} records.`
                }
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowExportModal(false)} className="text-[#A1A1AA]">
              Cancel
            </Button>
            <Button 
              onClick={handleExportData} 
              disabled={exporting}
              className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90"
            >
              {exporting ? 'Exporting...' : 'Download CSV'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clear Test Data Modal */}
      <Dialog open={showClearDataModal} onOpenChange={setShowClearDataModal}>
        <DialogContent className="bg-[#2B2B2B] border-white/10 text-[#F2F2F2]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="w-5 h-5" />
              Clear Test Data
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              This action cannot be undone
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-sm text-red-300">
                This will permanently delete all records with names/locations prefixed with &quot;TEST_&quot;. 
                This includes test medics, shifts, users, and expenses.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowClearDataModal(false)} className="text-[#A1A1AA]">
              Cancel
            </Button>
            <Button 
              onClick={handleClearTestData} 
              disabled={clearing}
              className="bg-red-500 hover:bg-red-600"
            >
              {clearing ? 'Clearing...' : 'Clear Test Data'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Logo Confirmation */}
      <AlertDialog open={showRemoveLogoDialog} onOpenChange={setShowRemoveLogoDialog}>
        <AlertDialogContent
          className="bg-[#2B2B2B] border-white/10 text-[#F2F2F2]"
          data-testid="remove-logo-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Remove organisation logo?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A1A1AA]">
              The stored logo file will be deleted. Your organisation name will
              continue to appear across the app. You can upload a new logo at
              any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="bg-transparent border-white/10 text-[#F2F2F2] hover:bg-white/5"
              data-testid="remove-logo-cancel"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveLogo}
              disabled={saving}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="remove-logo-confirm"
            >
              {saving ? 'Removing…' : 'Remove logo'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
